import { Slot, Tooltip } from 'radix-ui';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';

import { useSidebarCollapsed } from './sidebarCollapse';

type SidebarTooltipProps = {
  label: string;
  children: ReactNode;
  /** Показывать и в развёрнутом сайдбаре — у кнопки только значок. */
  always?: boolean;
} & Omit<ComponentPropsWithoutRef<'button'>, 'children'>;

/**
 * Подпись пункта свёрнутого сайдбара — справа от иконки, по наведению и
 * фокусу. Контент в портале: у сайдбара overflow-hidden, внутри подпись бы
 * обрезалась. В развёрнутом виде подпись и так видна — подсказки нет.
 *
 * Пропсы и ref пробрасываются в триггер: обёртку можно класть и внутрь
 * чужого `asChild` (DialogTrigger у «Встреч», DropdownMenuTrigger у «Отделов»).
 * Provider — в DesktopSidebar: одна задержка на все пункты, по соседним
 * иконкам подписи переключаются без повторного ожидания.
 */
export const SidebarTooltip = forwardRef<
  HTMLButtonElement,
  SidebarTooltipProps
>(function SidebarTooltip({ label, children, always, ...triggerProps }, ref) {
  const collapsed = useSidebarCollapsed();

  if (!collapsed && !always) {
    return (
      <Slot.Root {...triggerProps} ref={ref}>
        {children}
      </Slot.Root>
    );
  }

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild {...triggerProps} ref={ref}>
        {children}
      </Tooltip.Trigger>
      <Tooltip.Portal>
        {/* Свёрнутый: справа от узкой колонки. Развёрнутый (always): сверху,
            чтобы не наезжать на соседние кнопки сайдбара. */}
        <Tooltip.Content
          side={collapsed ? 'right' : 'top'}
          sideOffset={10}
          collisionPadding={8}
          className="z-[80] max-w-[260px] rounded-lg bg-[#102653] px-2.5 py-1.5 text-[12px] font-normal leading-snug text-white shadow-[0_8px_22px_rgba(16,38,83,0.25)]"
        >
          {label}
          <Tooltip.Arrow className="fill-[#102653]" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
});
